import { adminApiFetch } from '@/services/adminApiFetch';

// ─── Summary ────────────────────────────────────────────────────────────────

export interface CurrencyAmounts {
  BRL: number;
  EUR: number;
}

export interface SubscriptionCounts {
  pending: number;
  trialing: number;
  active: number;
  past_due: number;
  canceled: number;
  unpaid: number;
}

export interface TierMix {
  pro: number;
  business: number;
  custom: number;
}

export interface MetricsSummary {
  mrr_cents: CurrencyAmounts;
  normalized_mrr_cents: number;
  normalized_currency: 'EUR';
  normalize_rate: number;
  arr_cents: CurrencyAmounts;
  normalized_arr_cents: number;
  arpa_cents: CurrencyAmounts;
  counts: SubscriptionCounts;
  tier_mix: TierMix;
}

// ─── Funnel ──────────────────────────────────────────────────────────────────

export type MetricsWindow = 'month' | '30d';

export interface MetricsFunnel {
  window: MetricsWindow;
  since: string;
  signups: number;
  trials: number;
  paid: number;
  churned: number;
  signup_to_trial_pct: number;
  trial_to_paid_pct: number;
  signup_to_paid_pct: number;
}

// ─── Churn ───────────────────────────────────────────────────────────────────

export interface MetricsChurn {
  window: MetricsWindow;
  since: string;
  churned_count: number;
  payers_at_start: number;
  logo_churn_pct: number;
  mrr_churned_cents: CurrencyAmounts;
  mrr_new_cents: CurrencyAmounts;
  mrr_expansion_cents: CurrencyAmounts;
  mrr_contraction_cents: CurrencyAmounts;
  mrr_net_cents: CurrencyAmounts;
}

// ─── Cohorts ─────────────────────────────────────────────────────────────────

export interface CohortPoint {
  month_n: number;
  active_count: number;
  trialing_count: number;
  past_due_count: number;
  canceled_count: number;
  active_mrr_cents: number;
  snapshot_date: string;
}

export interface Cohort {
  signup_cohort: string;
  currency: 'BRL' | 'EUR';
  points: CohortPoint[];
}

export interface MetricsCohorts {
  months: number;
  cohorts: Cohort[];
}

// ─── Margin ──────────────────────────────────────────────────────────────────

export interface MetricsMargin {
  window: MetricsWindow;
  since: string;
  mrr_cents: CurrencyAmounts;
  normalized_mrr_cents: number;
  llm_cost_production_usd: string;
  llm_cost_preview_usd: string;
  llm_cost_production_normalized_cents: number;
  gross_margin_normalized_cents: number;
  note: 'aprox';
}

// ─── LLM unit economics ──────────────────────────────────────────────────────

export interface LlmTierSplit {
  fast: number;
  capable: number;
}

export interface LlmTenantOutlier {
  tenant_id: number;
  slug: string;
  cost_usd: string;
  ratio: number;
}

export interface MetricsLlm {
  window: MetricsWindow;
  since: string;
  /** 6dp string; null when there are no qualifying prod reply rows. */
  cost_per_message_usd: string | null;
  /** Ratio 0-1; null means "not reported" — must NOT be displayed as 0%. */
  cache_hit_ratio: number | null;
  messages: number;
  tier_split: LlmTierSplit;
  tenant_outliers: LlmTenantOutlier[];
}

// ─── Quota pressure ──────────────────────────────────────────────────────────

export interface QuotaTenant {
  tenant_id: number;
  business_name: string;
  subscription_tier: string;
  monthly_message_quota: number;
  messages_used_current_period: number;
  used_pct: number;
}

export interface MetricsQuotaPressure {
  tenants: QuotaTenant[];
}

// ─── Events ──────────────────────────────────────────────────────────────────

export interface SubscriptionEvent {
  id: number;
  tenant_id: number;
  event_type: string;
  from_status: string | null;
  to_status: string;
  tier: string | null;
  mrr_cents: number;
  currency: string;
  occurred_at: string;
}

export interface MetricsEvents {
  events: SubscriptionEvent[];
}

// ─── Gates ───────────────────────────────────────────────────────────────────

export type ChurnTrend = 'down' | 'up' | 'flat' | 'insufficient_data';
export type MetricsStage = 'pre_s0' | 'S0' | 'S1' | 'S2';

export interface CohortChurnTrend {
  trend: ChurnTrend;
  monthly_logo_churn_pcts: Array<{ month: string; churn_pct: number }>;
}

export interface MetricsGates {
  paying_count: number;
  cold_paying_count: number;
  cohort_churn: CohortChurnTrend;
  cac_cents: number | null;
  payback_months: number | null;
  stage: MetricsStage;
}

// ─── Acquisition cohorts (F133) ──────────────────────────────────────────────

/**
 * Precedence layer the attribution key was derived from:
 * promo_code ('code') → acquisition_ref ('ref') → acquisition_source
 * ('source', self-report) → 'organic' (no signal).
 */
export type AttributionLayer = 'code' | 'ref' | 'source' | 'organic';

export interface AcquisitionCohortRow {
  /** First-of-month signup cohort date, e.g. "2026-03-01". */
  cohort: string;
  signups: number;
  trials_started: number;
  converted: number;
  active_now: number;
  /** Month-offset (1..12) → logo churn pct for that offset. */
  churn_by_month: Record<number, number>;
  /**
   * Zero-filled per-currency dict (mirrors the `mrr_cents` convention).
   * Only meaningful on `layer === 'code'` rows — the "aprox" badge/columns
   * gating is a frontend concern, per the service docstring.
   */
  commission_accrued_cents: CurrencyAmounts;
}

export interface AcquisitionCohortGroup {
  key: string;
  layer: AttributionLayer;
  cohorts: AcquisitionCohortRow[];
}

export interface AcquisitionCohortsTotals {
  signups: number;
  commission_accrued_cents: CurrencyAmounts;
}

export interface MetricsAcquisitionCohorts {
  months: number;
  generated_for: string;
  groups: AcquisitionCohortGroup[];
  totals: AcquisitionCohortsTotals;
}

// ─── CAC config ──────────────────────────────────────────────────────────────

export interface CacConfigResponse {
  cac_cents: number;
}

// ─── Service object ──────────────────────────────────────────────────────────

export const adminMetricsService = {
  getSummary(): Promise<MetricsSummary> {
    return adminApiFetch<MetricsSummary>('/metrics/summary');
  },

  getFunnel(window: MetricsWindow = 'month'): Promise<MetricsFunnel> {
    return adminApiFetch<MetricsFunnel>(`/metrics/funnel?window=${window}`);
  },

  getChurn(window: MetricsWindow = 'month'): Promise<MetricsChurn> {
    return adminApiFetch<MetricsChurn>(`/metrics/churn?window=${window}`);
  },

  getCohorts(months = 12): Promise<MetricsCohorts> {
    return adminApiFetch<MetricsCohorts>(`/metrics/cohorts?months=${months}`);
  },

  getMargin(window: MetricsWindow = 'month'): Promise<MetricsMargin> {
    return adminApiFetch<MetricsMargin>(`/metrics/margin?window=${window}`);
  },

  getLlmUnitEconomics(window: MetricsWindow = 'month'): Promise<MetricsLlm> {
    return adminApiFetch<MetricsLlm>(`/metrics/llm?window=${window}`);
  },

  getQuotaPressure(): Promise<MetricsQuotaPressure> {
    return adminApiFetch<MetricsQuotaPressure>('/metrics/quota-pressure');
  },

  getEvents(limit = 50): Promise<MetricsEvents> {
    return adminApiFetch<MetricsEvents>(`/metrics/events?limit=${limit}`);
  },

  getGates(): Promise<MetricsGates> {
    return adminApiFetch<MetricsGates>('/metrics/gates');
  },

  getAcquisitionCohorts(months = 12): Promise<MetricsAcquisitionCohorts> {
    return adminApiFetch<MetricsAcquisitionCohorts>(
      `/metrics/acquisition-cohorts?months=${months}`
    );
  },

  updateCac(cac_cents: number): Promise<CacConfigResponse> {
    return adminApiFetch<CacConfigResponse>('/config/cac', {
      method: 'PUT',
      body: JSON.stringify({ cac_cents }),
    });
  },
};
